import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Loader2, Plus, Star, StarOff, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import {
  deleteListingPhoto,
  fetchListingPhotos,
  uploadListingPhoto,
} from "../utils/listingPhotos";
import ConfirmDialog from "./ConfirmDialog";

const MAX_FILE_MB = 10;

export default function ListingGallery({
  listingId,
  primaryPhotoUrl,
  onPrimaryChange,
}) {
  const currentUser = useSelector((state) => state.userObject);

  const fileInputRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const refresh = async () => {
    try {
      const list = await fetchListingPhotos(listingId);
      setPhotos(list);
    } catch (err) {
      console.error("[ListingGallery] fetch failed:", err);
      toast.error("Couldn't load gallery photos.");
    }
  };

  useEffect(() => {
    if (!listingId) return;
    let active = true;
    (async () => {
      setIsLoading(true);
      try {
        const list = await fetchListingPhotos(listingId);
        if (active) setPhotos(list);
      } catch (err) {
        console.error(err);
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [listingId]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (files.length === 0) return;

    const oversized = files.filter((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (oversized.length > 0) {
      toast.error(`${oversized.length} file(s) exceed ${MAX_FILE_MB} MB.`);
      return;
    }

    setIsUploading(true);
    setUploadingCount(files.length);
    let successes = 0;
    try {
      await Promise.all(
        files.map(async (file, index) => {
          try {
            await uploadListingPhoto({
              listingId,
              file,
              currentUser,
              index,
            });
            successes += 1;
          } catch (err) {
            console.error("[ListingGallery] upload failed:", err);
          }
        }),
      );
      await refresh();
      if (successes === files.length) {
        toast.success(
          successes === 1
            ? "Photo uploaded."
            : `${successes} photos uploaded.`,
        );
      } else if (successes > 0) {
        toast.success(`Uploaded ${successes} of ${files.length} photos.`);
      } else {
        toast.error("Upload failed.");
      }
    } finally {
      setIsUploading(false);
      setUploadingCount(0);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await deleteListingPhoto({ photo: pendingDelete, listingId });
      // If we just deleted the current primary, clear it on the listing.
      if (primaryPhotoUrl && primaryPhotoUrl === pendingDelete.url) {
        onPrimaryChange?.("");
      }
      await refresh();
      toast.success("Photo deleted.");
      setPendingDelete(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete photo.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSetPrimary = (photo) => {
    onPrimaryChange?.(photo.url);
    toast.success("Set as primary photo.");
  };

  const hasPrimary = !!primaryPhotoUrl;
  const isPrimaryMissingFromGallery =
    hasPrimary && !photos.some((p) => p.url === primaryPhotoUrl);

  return (
    <section className="bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm overflow-hidden">
      <header className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-leechiuBlue m-0">
            Photo gallery
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {isLoading
              ? "Loading…"
              : `${photos.length} photo${photos.length === 1 ? "" : "s"} · shared with Raven (photos collection + Storage)`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isUploading ? (
            <span className="text-xs text-gray-500 inline-flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Uploading {uploadingCount}…
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-leechiuOrange text-white text-xs font-medium hover:opacity-90 disabled:opacity-60"
          >
            <Plus className="w-3.5 h-3.5" />
            Add photos
          </button>
        </div>
      </header>

      {isPrimaryMissingFromGallery ? (
        <div className="px-5 py-2.5 text-xs text-amber-800 bg-amber-50 border-b border-amber-100">
          The current primary photo isn't in this listing's photo
          collection. It may have been set to an external URL or its
          record was removed.
        </div>
      ) : null}

      <div className="p-5">
        {isLoading ? (
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[4/3] rounded-lg bg-gray-100 animate-pulse"
              />
            ))}
          </div>
        ) : photos.length === 0 ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="w-full bg-gray-50 border border-dashed border-gray-200 rounded-xl py-10 px-4 flex flex-col items-center gap-2 hover:bg-white hover:border-leechiuBlue/30 transition-colors"
          >
            <Plus className="w-6 h-6 text-leechiuBlue" />
            <span className="text-sm font-medium text-leechiuBlue">
              Add the first photo
            </span>
            <span className="text-xs text-gray-500">
              PNG, JPG, WebP — up to {MAX_FILE_MB} MB each
            </span>
          </button>
        ) : (
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
            {photos.map((photo) => {
              const isPrimary = photo.url === primaryPhotoUrl;
              const uploaderLabel =
                photo.uploadedBy?.displayName ||
                photo.uploadedBy?.email ||
                null;
              return (
                <div
                  key={photo.id}
                  className={`group relative aspect-[4/3] rounded-lg overflow-hidden bg-slate-100 ring-1 ${
                    isPrimary ? "ring-leechiuOrange ring-2" : "ring-gray-200"
                  }`}
                  title={uploaderLabel ? `Uploaded by ${uploaderLabel}` : ""}
                >
                  <a
                    href={photo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full h-full"
                  >
                    <img
                      src={photo.url}
                      alt=""
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </a>
                  {isPrimary ? (
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-leechiuOrange text-white text-[10px] font-semibold uppercase tracking-wide">
                      <Star className="w-2.5 h-2.5" />
                      Primary
                    </span>
                  ) : null}
                  <div className="absolute inset-x-0 bottom-0 flex items-stretch gap-1 p-1.5 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    {isPrimary ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          onPrimaryChange?.("");
                        }}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded bg-white/95 text-leechiuBlue text-[11px] font-medium hover:bg-white"
                      >
                        <StarOff className="w-3 h-3" />
                        Unset
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          handleSetPrimary(photo);
                        }}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded bg-white/95 text-leechiuBlue text-[11px] font-medium hover:bg-white"
                      >
                        <Star className="w-3 h-3" />
                        Set primary
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setPendingDelete(photo);
                      }}
                      className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded bg-red-600/95 text-white text-[11px] font-medium hover:bg-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this photo?"
        message="This removes the photo record from the shared photos collection plus its original file and auto-generated variants from Storage. The website gallery will reflect the change after the next cache refresh."
        confirmLabel={isDeleting ? "Deleting…" : "Delete"}
        destructive
        onConfirm={handleDeleteConfirm}
        onCancel={() => !isDeleting && setPendingDelete(null)}
      />
    </section>
  );
}
